import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Plus, X, Save, Loader } from "lucide-react";
import {
  getUserPortfolioNew,
  updatePortfolioNew,
} from "../views/utilities/ApiCalls";
import { getTokenDetails } from "../views/utilities/CommonFunction";
import { Toaster, toast } from "sonner";

const EditPortfolio = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [portfolioData, setPortfolioData] = useState({
    bookName: "",
    authorName: "",
    quotes: [""],
    achievements: [""],
  });

  useEffect(() => {
    fetchPortfolioData();
  }, []);

  const fetchPortfolioData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("loginDetails");
      const { _id: userId } = getTokenDetails(token);

      const response = await getUserPortfolioNew(userId);

      if (response.status === "Success" && response.data) {
        const data = response.data;
        setPortfolioData({
          bookName: data.BookName || "",
          authorName: data.AuthorName || "",
          quotes: data.Quotes && data.Quotes.length > 0 ? data.Quotes : [""],
          achievements:
            data.Achievements && data.Achievements.length > 0
              ? data.Achievements
              : [""],
        });
      }
    } catch (error) {
      console.error("Error fetching portfolio data:", error);
      toast.error("Failed to load portfolio data");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value, index = null) => {
    if (index !== null) {
      // Handle array fields (quotes, achievements)
      const newArray = [...portfolioData[field]];
      newArray[index] = value;
      setPortfolioData((prev) => ({
        ...prev,
        [field]: newArray,
      }));
    } else {
      // Handle single fields (bookName, authorName)
      setPortfolioData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const addField = (field) => {
    setPortfolioData((prev) => ({
      ...prev,
      [field]: [...prev[field], ""],
    }));
  };

  const removeField = (field, index) => {
    if (portfolioData[field].length > 1) {
      const newArray = portfolioData[field].filter((_, i) => i !== index);
      setPortfolioData((prev) => ({
        ...prev,
        [field]: newArray,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      const token = localStorage.getItem("loginDetails");
      const { _id: userId } = getTokenDetails(token);

      // Filter out empty strings from arrays
      const filteredQuotes = portfolioData.quotes.filter(
        (quote) => quote.trim() !== ""
      );
      const filteredAchievements = portfolioData.achievements.filter(
        (achievement) => achievement.trim() !== ""
      );

      const updateData = {
        userId: userId,
        action: "replace",
        BookName: portfolioData.bookName.trim(),
        AuthorName: portfolioData.authorName.trim(),
        Quotes: filteredQuotes,
        Achievements: filteredAchievements,
      };

      const response = await updatePortfolioNew(updateData);

      if (response.status === "Success") {
        toast.success("eBook updated successfully!");
      } else {
        toast.error(response.message || "Failed to update eBook");
      }
    } catch (error) {
      console.error("Error updating eBook:", error);
      toast.error("Failed to update eBook");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PortfolioContainer>
        <div className="loading-container">
          <Loader className="spinner" size={40} />
          <p>Loading eBook data...</p>
        </div>
      </PortfolioContainer>
    );
  }

  return (
    <PortfolioContainer>
      <div className="portfolio-header">
        <h2 className="header-title">
          {t("Settings.EditPortfolio", "Edit eBook")}
        </h2>
        <p className="header-subtitle">
          Manage your eBook details including book information, quotes, and
          achievements.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="portfolio-form">
        {/* Book Name Field */}
        <div className="form-group">
          <label htmlFor="bookName" className="label">
            BOOK NAME
          </label>
          <input
            type="text"
            id="bookName"
            value={portfolioData.bookName}
            onChange={(e) => handleInputChange("bookName", e.target.value)}
            placeholder="Enter your book name"
            className="form-input"
          />
        </div>

        {/* Author Name Field */}
        <div className="form-group">
          <label htmlFor="authorName" className="label">
            AUTHOR NAME
          </label>
          <input
            type="text"
            id="authorName"
            value={portfolioData.authorName}
            onChange={(e) => handleInputChange("authorName", e.target.value)}
            placeholder="Enter author name"
            className="form-input"
          />
        </div>

        {/* Quotes Section */}
        <div className="form-group">
          <label className="label">QUOTES</label>
          <div className="dynamic-fields">
            {portfolioData.quotes.map((quote, index) => (
              <div key={index} className="field-row">
                <div className="input-wrapper">
                  <input
                    type="text"
                    value={quote}
                    onChange={(e) =>
                      handleInputChange("quotes", e.target.value, index)
                    }
                    placeholder="Enter a quote"
                    className="form-input"
                  />
                  {portfolioData.quotes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeField("quotes", index)}
                      className="remove-btn"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addField("quotes")}
              className="add-btn"
            >
              <Plus size={16} />
              Add More Quotes
            </button>
          </div>
        </div>

        {/* Achievements Section */}
        <div className="form-group">
          <label className="label">ACHIEVEMENTS</label>
          <div className="dynamic-fields">
            {portfolioData.achievements.map((achievement, index) => (
              <div key={index} className="field-row">
                <div className="input-wrapper">
                  <input
                    type="text"
                    value={achievement}
                    onChange={(e) =>
                      handleInputChange("achievements", e.target.value, index)
                    }
                    placeholder="Enter an achievement"
                    className="form-input"
                  />
                  {portfolioData.achievements.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeField("achievements", index)}
                      className="remove-btn"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addField("achievements")}
              className="add-btn"
            >
              <Plus size={16} />
              Add More Achievements
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <div className="form-actions">
          <button type="submit" disabled={saving} className="submit-btn">
            {saving ? (
              <>
                <Loader className="spinner" size={16} />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save eBook
              </>
            )}
          </button>
        </div>
      </form>

      <Toaster />
    </PortfolioContainer>
  );
};

const PortfolioContainer = styled.div`
  color: var(--color-text);
  padding: 0px;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;

    .spinner {
      animation: spin 1s linear infinite;
      margin-bottom: 20px;
    }

    p {
      color: var(--color-text-tertiary);
      margin: 0;
    }
  }

  .portfolio-header {
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid var(--color-border);

    .header-title {
      margin: 0 0 10px 0;
      font-size: 32px;
      font-weight: 700;
      font-family: serif;
      color: var(--color-text);
    }

    .header-subtitle {
      color: var(--color-text-tertiary);
      margin: 0;
      line-height: 1.5;
      font-size: 14px;
    }
  }

  .portfolio-form {
    background: transparent;
    border-radius: 0;
    padding: 0;
    border: none;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .form-group {
    margin-bottom: 0px;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .label {
      display: block;
      color: var(--color-text-tertiary);
      font-weight: 600;
      margin-bottom: 0px;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .form-input {
      width: 100%;
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: 4px;
      padding: 6px 10px;
      color: var(--color-text);
      font-size: 14px;
      transition: border-color 0.2s;

      &:focus {
        outline: none;
        border-color: var(--color-text);
      }

      &::placeholder {
        color: var(--color-text-tertiary);
      }
    }
  }

  .dynamic-fields {
    .field-row {
      margin-bottom: 6px;

      .input-wrapper {
        display: flex;
        align-items: center;
        width: 100%;
        position: relative;
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        border-radius: 4px;
        transition: border-color 0.2s;

        &:focus-within {
          border-color: var(--color-text);
        }
      }

      .form-input {
        flex: 1;
        border: none;
        background: transparent;
        padding-right: 40px; /* Space for the button */

        &:focus {
          border: none;
          outline: none;
        }
      }

      .remove-btn {
        position: absolute;
        right: 4px;
        top: 50%;
        transform: translateY(-50%);
        background: transparent;
        border: none;
        border-radius: 4px;
        padding: 4px;
        color: var(--color-error);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background-color 0.2s;
        height: 28px;
        width: 28px;

        &:hover {
          background: var(--color-background);
        }
      }
    }

    .add-btn {
      background: transparent;
      border: 1px dashed var(--color-border);
      border-radius: 4px;
      padding: 8px 16px;
      color: var(--color-text-secondary);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      font-size: 13px;
      transition: all 0.2s;
      margin-top: 5px;
      width: 100%;

      &:hover {
        background: var(--color-surface);
        color: var(--color-text);
        border-color: var(--color-text-secondary);
      }
    }
  }

  .form-actions {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
    padding-top: 12px;
    border-top: 1px solid var(--color-border);

    .submit-btn {
      background: var(--color-primary);
      border: none;
      border-radius: 4px;
      padding: 10px 24px;
      color: var(--color-surface);
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      font-weight: 600;
      transition: all 0.2s;

      &:hover:not(:disabled) {
        opacity: 0.9;
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .spinner {
        animation: spin 1s linear infinite;
      }
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 768px) {
    padding: 15px;

    .portfolio-form {
      padding: 20px;
    }

    .dynamic-fields .field-row {
      flex-direction: column;
      align-items: stretch;

      .remove-btn {
        align-self: flex-end;
        width: fit-content;
      }
    }
  }
`;

export default EditPortfolio;
